<template>
	<div class="header-layout flex flex-col" :class="{ 'header-layout--has-cogicon' : hasCogicon }">
		<div class="header-layout__header shrink-0 border-b relative" :class="{ 'border-transparent' : scrollPos === 0}">
			<CMobileUpButton v-if="isMobile && hasCogicon" class="top-0 left-0" />
			<div class="header-layout__header-inner flex items-center justify-between gap-4">
				<div class="header-layout__title flex items-center gap-4">
					<slot name="title" />
				</div>
				<CSupportAndAvatar
					v-if="!isMobile && globalStore.avatar"
					:avatar="globalStore.avatar"
					size="m"
					class="header-layout__avatar"
					source-context="header-layout"
					@show-account-menu="$emit('show-account-menu', $event)"
				/>
			</div>
		</div>
		<div
			:class="{'header-layout__scroll grow scrollbar scrollbar--wide': allowScrolling}"
			@scroll="onScroll"
		>
			<div class="header-layout__content">
				<slot />
			</div>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, ref, computed, inject } from 'vue';
	import { globalStore } from 'src/stores/global-store';
	import CMobileUpButton from 'src/components/navigation/MobileUpButton.vue';
	import CSupportAndAvatar from 'src/components/navigation/SupportAndAvatar.vue';
	import { isMobileKey } from 'src/utilities/injection-keys';

	export default defineComponent({
		name: 'HeaderLayout',
		components: {
			CMobileUpButton,
			CSupportAndAvatar
		},
		props: {
			hasCogicon: { type: Boolean, default: false },
			disableScroll: { type: Boolean, default: false }
		},
		emits: ['show-account-menu'],
		setup(props) {
			const isMobile = inject(isMobileKey);
			const scrollPos = ref(0);
			const allowScrolling = computed(() => !isMobile.value && !props.disableScroll);
			const onScroll = (e) => {
				scrollPos.value = e.target.scrollTop;
			};

			return {
				scrollPos,
				allowScrolling,
				onScroll,
				globalStore,
				isMobile
			};
		}
	});
</script>

<style lang="scss">
.header-layout {
	--content-top-padding: #{$space__4};
	--content-right-padding: #{$space__4};
	--content-bottom-padding: #{$space__4};
	--content-left-padding: #{$space__4};
	width: 100%;

	.admin--desktop.admin--nav-collapsed & {
		--content-left-padding: calc(#{$space__4} + env(safe-area-inset-left, 0px));
	}

	.admin--desktop & {
		height: 100vh;
	}

	.support-and-avatar {
		margin-top: calc(var(--space__touch-target) * -1px);
	}

	&--has-cogicon {

		.admin--mobile & .header-layout__header {
			padding-left: 0;

			&-inner {
				width: calc(100% - $mobile-up-button-width);
				transform: translateX($mobile-up-button-width);
			}
		}

		.header-layout__header-inner {
			// allow support/avatar buttons to have a large click area without pushing heading too tall
			height: 30px;

			.admin--desktop.admin--nav-collapsed & {
				width: calc(100% - var(--cogicon-space));
				transform: translateX(var(--cogicon-space));
			}
		}
	}

	&__header {
		padding: var(--content-top-padding) var(--content-right-padding) var(--content-bottom-padding) var(--content-left-padding);

		h1 {
			margin: 0;
		}
	}

	.admin--mobile &__title {
		justify-content: space-between;
		width: 100%;
	}

	&__content {
		margin-top: var(--content-top-padding);
		margin-bottom: var(--content-bottom-padding);
		padding-right: calc(var(--content-right-padding) + env(safe-area-inset-right, 0px));
		padding-bottom: $space__4;
		padding-left: var(--content-left-padding);

		@media (max-width: 1350px) {
			padding-bottom: $space__6;
		}
	}

	&__scroll {
		scroll-behavior: smooth;
	}
}
</style>